import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getCookie } from './getCookie';

export const API_BASE_URLS = {
  main: import.meta.env.VITE_AI_BASE_URL_API || 'https://bprm-ai-api.workfinity.com/api/forms-service',
  auth: import.meta.env.VITE_API_BASE_URL_BPRM || 'https://bprm-ai-api.workfinity.com/api',
} as const;


const LOGIN_URL = 'https://bprm-ai-portal.workfinity.com/login';

// Response types
export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  access_token?: string;
  token_type?: string;
}

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  client_id: string;
}

// Axios instance creator
function createAxiosInstance(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 120000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add CSRF token to every request
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const csrfToken = getCookie('csrf_token');
      if (csrfToken && config.headers) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }

      const appId = getCookie("app_id") || "1";
      config.headers["X-App-Id"] = appId;
      return config;
    },
    (error) => Promise.reject(error)
  );

  return instance;
}

// Create instances for different base URLs
export const apiClient = createAxiosInstance(API_BASE_URLS.main);
export const authClient = createAxiosInstance(API_BASE_URLS.auth);
console.log("apiClient", API_BASE_URLS.main)
console.log("authClient", API_BASE_URLS.auth)
// Token refresh state management
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

export const redirectToLogin = () => {
  // window.location.href = LOGIN_URL;
};

export const refreshToken = async (): Promise<boolean> => {
  try {
    const response = await authClient.post<RefreshTokenResponse>('/auth/refresh');
    return response.data.success;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

export const handleLogout = async () => {
  try {
    await authClient.post('/auth/logout');
  } catch (error) {
    console.error('Logout failed:', error);
  } finally {
    redirectToLogin();
  }
};

const setupResponseInterceptor = (client: AxiosInstance) => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (
        (error.response?.status === 401 || error.response?.status === 403) &&
        originalRequest &&
        !originalRequest._retry
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((token: string) => {
              if (originalRequest.headers) {
                resolve(client(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshSuccess = await refreshToken();

          if (refreshSuccess) {
            isRefreshing = false;
            onRefreshed('success');
            return client(originalRequest);
          } else {
            isRefreshing = false;
            await handleLogout();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          isRefreshing = false;
          await handleLogout();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

setupResponseInterceptor(apiClient);
setupResponseInterceptor(authClient);

export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || 'An error occurred';
  }
  return error instanceof Error ? error.message : 'An unknown error occurred';
};